/* Misumi Collection Logic (Sidebar + L5 Grid) */
(() => {
    // Shared Cache
    const globalCache = window.__MISUMI_TREE_CACHE__ || (window.__MISUMI_TREE_CACHE__ = { data: null, promise: null });

    // --- Utils ---
    const getEl = (sel) => document.querySelector(sel);
    const createEl = (tag, cls) => {
        const el = document.createElement(tag);
        if (cls) el.className = cls;
        return el;
    };

    async function loadTree(url) {
        if (globalCache.data) return globalCache.data;
        if (globalCache.promise) return globalCache.promise;
        globalCache.promise = fetch(url).then(r => r.json()).then(d => {
            globalCache.data = d;
            return d;
        });
        return globalCache.promise;
    }

    function findNodeByHandle(tree, handle) {
        // Quick lookup if exists in meta (O(1))
        if (tree.meta[handle]) return { handle, ...tree.meta[handle] };
        return null;
    }

    // --- Shared helpers ---
    function buildParentMap(tree) {
        const parentMap = {};
        Object.keys(tree).forEach(key => {
            if (key === 'meta' || key === 'generated_at') return;
            const children = tree[key];
            if (Array.isArray(children)) {
                children.forEach(c => parentMap[c] = key);
            }
        });
        return parentMap;
    }

    function buildLineage(tree, currentHandle, parentMap) {
        let cursor = currentHandle;
        const lineage = [cursor];
        while (parentMap[cursor]) {
            cursor = parentMap[cursor];
            lineage.unshift(cursor);
        }

        // Orphan recovery: if root is not L1, try to find L1 parent by handle prefix
        let rootL1 = lineage[0];
        const rootMeta = tree.meta[rootL1];
        if (rootMeta && rootMeta.depth > 1 && !parentMap[rootL1]) {
            const l1Candidates = Object.keys(tree.meta).filter(h => tree.meta[h].depth === 1);
            let bestLen = 0, foundL1 = null;
            const handlePrefix = rootL1.replace(/[0-9]+$/, '');
            for (const l1h of l1Candidates) {
                let common = 0;
                while (common < l1h.length && common < rootL1.length && l1h[common] === rootL1[common]) common++;
                if (common > bestLen && common >= 5) { bestLen = common; foundL1 = l1h; }
            }
            if (foundL1) lineage.unshift(foundL1);

            // Also try to find intermediate depth nodes between L1 and current
            const currentMeta = tree.meta[currentHandle];
            if (currentMeta && currentMeta.code && foundL1) {
                const currentCode = currentMeta.code;
                const cPrefix = currentCode.replace(/0+$/, '');
                for (let d = 2; d < currentMeta.depth; d++) {
                    let bestMatch = null, bestLen = 0;
                    Object.keys(tree.meta).forEach(h => {
                        const m = tree.meta[h];
                        if (m.depth !== d || !m.code) return;
                        const iPrefix = m.code.replace(/0+$/, '');
                        if (cPrefix.startsWith(iPrefix) && iPrefix.length > bestLen && cPrefix.length > iPrefix.length) {
                            bestLen = iPrefix.length;
                            bestMatch = h;
                        }
                    });
                    if (bestMatch && !lineage.includes(bestMatch)) {
                        lineage.splice(lineage.length - 1, 0, bestMatch);
                    }
                }
            }
        }
        return lineage;
    }

    // --- Sidebar Logic ---
    function renderSidebar(tree, currentHandle) {
        const container = getEl('[data-misumi-sidebar-root]');
        if (!container) return;
        const content = container.querySelector('.misumi-tree__content') || container;

        let node = findNodeByHandle(tree, currentHandle);
        if (!node) {
            container.style.display = 'none';
            return;
        }
        container.style.display = '';

        const parentMap = buildParentMap(tree);
        const lineage = buildLineage(tree, currentHandle, parentMap);

        let rootL1 = lineage[0];
        const rootMeta = tree.meta[rootL1];

        // --- Collect L2 siblings ---
        // Primary: use tree[rootL1] (direct children array).
        let l2Nodes = tree[rootL1] || [];

        // If orphan node is depth-2 and not in l2Nodes, merge all depth-2 orphans that share
        // the same handle prefix (e.g., all "col-m*" depth-2 nodes are siblings under "col-mech").
        if (rootMeta && rootMeta.depth === 2 && !l2Nodes.includes(currentHandle)) {
            const handlePfx = currentHandle.replace(/[0-9]+$/, ''); // "col-m"
            const orphanSiblings = Object.keys(tree.meta).filter(h => {
                const m = tree.meta[h];
                return m.depth === 2 && h.startsWith(handlePfx) && !parentMap[h];
            });
            // Merge: existing children + orphan siblings (dedup)
            const merged = new Set([...l2Nodes, ...orphanSiblings]);
            l2Nodes = [...merged];
        }

        if (l2Nodes.length === 0) {
            container.style.display = 'none';
            return;
        }

        const ul = createEl('ul', 'misumi-tree__list');

        l2Nodes.forEach(l2Handle => {
            const l2Meta = tree.meta[l2Handle];
            const isL2Active = lineage.includes(l2Handle);

            const li = createEl('li', 'misumi-tree__item');
            const link = createEl('a', 'misumi-tree__link');
            link.href = l2Meta.url;
            link.textContent = l2Meta.title;
            if (l2Handle === currentHandle) link.classList.add('is-active');

            li.appendChild(link);

            // Expand L3 if this L2 is active or in lineage
            if (isL2Active) {
                const l3Nodes = findChildrenOf(tree, l2Handle, parentMap);
                if (l3Nodes && l3Nodes.length) {
                    const ul3 = createEl('ul', 'misumi-tree__list misumi-tree__list--l3');
                    l3Nodes.forEach(l3Handle => {
                        const l3Meta = tree.meta[l3Handle];
                        if (!l3Meta) return; // safety
                        const isL3Active = lineage.includes(l3Handle);

                        const li3 = createEl('li', 'misumi-tree__item');
                        const link3 = createEl('a', 'misumi-tree__link');
                        link3.href = l3Meta.url;
                        link3.textContent = l3Meta.title;
                        if (l3Handle === currentHandle) link3.classList.add('is-active');

                        li3.appendChild(link3);

                        // Expand L4 if L3 is active
                        if (isL3Active) {
                            const l4Nodes = findChildrenOf(tree, l3Handle, parentMap);
                            if (l4Nodes && l4Nodes.length) {
                                const ul4 = createEl('ul', 'misumi-tree__list misumi-tree__list--l4');
                                l4Nodes.forEach(l4Handle => {
                                    const l4Meta = tree.meta[l4Handle];
                                    if (!l4Meta) return;
                                    const li4 = createEl('li', 'misumi-tree__item');
                                    const link4 = createEl('a', 'misumi-tree__link');
                                    link4.href = l4Meta.url;
                                    link4.textContent = l4Meta.title;
                                    if (l4Handle === currentHandle) link4.classList.add('is-active');

                                    li4.appendChild(link4);
                                    ul4.appendChild(li4);
                                });
                                li3.appendChild(ul4);
                            }
                        }
                        ul3.appendChild(li3);
                    });
                    li.appendChild(ul3);
                }
            }
            ul.appendChild(li);
        });

        // Render into content area (preserve header)
        content.innerHTML = "";
        content.appendChild(ul);
    }

    // --- Orphan child discovery ---
    // When tree[handle] has no direct children array, find children by code-prefix in meta.
    function findChildrenOf(tree, handle, parentMap) {
        let children = tree[handle];
        if (children && children.length > 0) return children;

        // Orphan recovery: find next-depth nodes whose handle shares prefix pattern
        const meta = tree.meta[handle];
        if (!meta || !meta.code) return [];
        const parentCode = meta.code; // e.g. "M0100000000"
        const parentDepth = meta.depth;
        // Strip trailing zeros to get the meaningful prefix
        // M0100000000 → "M01", M0101000000 → "M0101"
        const prefix = parentCode.replace(/0+$/, ''); // "M01"
        const childDepth = parentDepth + 1;

        return Object.keys(tree.meta).filter(h => {
            const m = tree.meta[h];
            if (m.depth !== childDepth) return false;
            if (!m.code) return false;
            const childPrefix = m.code.replace(/0+$/, '');
            // Child code must START with parent prefix and be longer
            return childPrefix.startsWith(prefix) && childPrefix.length > prefix.length;
        });
    }

    // --- Breadcrumb Hierarchy ---
    function renderBreadcrumbs(tree, currentHandle) {
        const nav = getEl('.breadcrumbs-container');
        if (!nav) return;

        const node = findNodeByHandle(tree, currentHandle);
        if (!node || node.depth <= 1) return;

        const parentMap = buildParentMap(tree);
        const lineage = buildLineage(tree, currentHandle, parentMap);

        // lineage = [L1, L2, ..., currentHandle]
        // We need to inject ancestors (all except the last = current) into the breadcrumb
        const ancestors = lineage.slice(0, -1);
        if (ancestors.length === 0) return;

        // Find the delimiter template from existing breadcrumb
        const existingDelimiter = nav.querySelector('.breadcrumbs-delimiter');
        if (!existingDelimiter) return;

        // Find the last child (current collection title <span>)
        // The current breadcrumb for collection pages is: Home > delimiter > <span>title</span>
        const lastSpan = nav.querySelector('span:last-of-type');
        if (!lastSpan) return;

        // Find the delimiter before lastSpan (the one that separates Home from title)
        const delimBefore = lastSpan.previousElementSibling;
        if (!delimBefore) return;

        // Build fragment: for each ancestor, add delimiter + link
        const frag = document.createDocumentFragment();
        ancestors.forEach(handle => {
            const meta = tree.meta[handle];
            if (!meta) return;

            const delim = existingDelimiter.cloneNode(true);
            const link = document.createElement('a');
            link.href = meta.url;
            link.textContent = meta.title.replace('产品', '產品');
            frag.appendChild(delim);
            frag.appendChild(link);
        });

        // Insert the ancestors AFTER the Home link but BEFORE the last delimiter + title
        // Current structure: <a>首頁</a> <delimiter> <span>title</span>
        // Target:  <a>首頁</a> <delimiter><a>L1</a> <delimiter><a>L2</a> <delimiter> <span>title</span>
        nav.insertBefore(frag, delimBefore);
    }

    // --- L5 Grid Logic ---
    function renderL5Grid(tree, currentHandle) {
        const container = getEl('[data-misumi-l5-root]');
        if (!container) return;

        const parentMap = {};
        Object.keys(tree).forEach(key => {
            if (key === 'meta' || key === 'generated_at') return;
            if (Array.isArray(tree[key])) tree[key].forEach(c => parentMap[c] = key);
        });

        const children = findChildrenOf(tree, currentHandle, parentMap);
        if (!children || children.length === 0) {
            container.style.display = 'none';
            return;
        }

        const grid = createEl('div', 'misumi-l5-grid__container');

        children.forEach(childHandle => {
            const meta = tree.meta[childHandle];
            const card = createEl('a', 'misumi-l5-card');
            card.href = meta.url;

            const title = createEl('div', 'misumi-l5-card__title');
            title.textContent = meta.title;

            // Optional: Show count or deeper children preview? User asked for L5 items.
            // For now, simple cards.

            card.appendChild(title);
            grid.appendChild(card);
        });

        // Set Header Title based on context
        const header = container.querySelector('.misumi-l5-grid__title');
        if (header) {
            header.textContent = "選擇分類"; // Localized
        }

        container.querySelector('.misumi-l5-body').innerHTML = "";
        container.querySelector('.misumi-l5-body').appendChild(grid);
        container.hidden = false;
    }

    // --- Init ---
    async function init() {
        const sidebarRoot = getEl('[data-misumi-sidebar-root]');
        const gridRoot = getEl('[data-misumi-l5-root]');
        const hasBreadcrumb = !!getEl('.breadcrumbs-container');

        if (!sidebarRoot && !gridRoot && !hasBreadcrumb) return;

        // Get handle from data attribute (also check breadcrumb context from URL)
        const currentHandle = (sidebarRoot?.dataset.handle || gridRoot?.dataset.handle);
        const treeUrl = (sidebarRoot?.dataset.tree || gridRoot?.dataset.tree);

        if (!treeUrl) return;
        if (!currentHandle) {
            if (sidebarRoot) sidebarRoot.style.display = 'none';
            return;
        }

        try {
            const tree = await loadTree(treeUrl);
            if (sidebarRoot) renderSidebar(tree, currentHandle);
            if (gridRoot) renderL5Grid(tree, currentHandle);
            renderBreadcrumbs(tree, currentHandle);
        } catch (e) {
            console.error("Misumi Collection Error:", e);
            if (sidebarRoot) sidebarRoot.style.display = 'none';
        }
    }

    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
    else init();
})();
